/* eslint-disable jsx-a11y/no-autofocus */
import React, { memo, useEffect, useState } from "react";
import { withRouter } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Modal } from "@talentbait/shared-components";

import {
  EditBasicInfo,
  EditBasicInfoContainer,
  EditBasicInfoText,
  JobTitle,
  JobTitleContainer,
  JobTitleInput,
  NavigationStepsButton,
  NavigationStepsContainer,
  NavigationStepsWrapper,
  SaveJobTitleButton,
  Step,
  StepJobTitleContainer,
  StepsContainer
} from "./NavigationSteps.styled";
import { CloseButton } from "../edition-modal/EditionModal.styled";
import {
  AnnouncementIcon,
  ArrowEnterIcon,
  BrushIcon,
  ConfigIcon,
  EditIcon,
  LongRightArrowIcon,
  RocketIcon,
  XIcon
} from "../../../Components/Icons";
import { HeaderDivider } from "../AdAssistant.styled";
import { useUrlBasedStep } from "../hooks/useUrlBasedStep";
import { useSaveCampaign } from "../hooks/useSaveCampaign";
import AdAssistantStore from "../AdAssistantStore";
import { withRefluxStore } from "../../../Stores";
import { checkCampaignStatus } from "../utils";
import EditCampaignDataModal from "../edit-campaign-data-modal/EditCampaignDataModal";
import { animationContainerStyle, animationVariants } from "./NavigationSteps.animation";

const NavigationSteps = ({
  location,
  match,
  history,
  step,
  onClose,
  campaignData,
  hideSteps = false
}) => {
  const { t } = useTranslation();
  const { campaignId } = match.params;
  const saveCampaign = useSaveCampaign(campaignId);
  const { stepName } = useUrlBasedStep({ location });

  const [editCampaignName, setEditCampaignName] = useState(false);
  const [newCampaignName, setNewCampaignName] = useState("");
  const [editBasicInfo, setEditBasicInfo] = useState(false);
  useEffect(() => {
    setNewCampaignName(campaignData.campaignName);
  }, [campaignData]);

  const handleNavigation = async (url) => {
    const newStatus = checkCampaignStatus(campaignData);
    await saveCampaign({ ...campaignData, step: stepName, status: newStatus });
    history.push(url);
  };

  const handleEditBasicInfo = () => {
    setEditCampaignName(!editCampaignName);
  };

  const handleCampaignNameChange = (e) => {
    setNewCampaignName(e.target.value);
  };

  const saveNewCampaignName = async () => {
    await saveCampaign({ ...campaignData, campaignName: newCampaignName });
    handleEditBasicInfo();
  };

  const renderJobTitleContainer = () => {
    return editCampaignName ? (
      <JobTitleContainer>
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={animationVariants}
          transition={{ duration: 0.5 }}
          style={animationContainerStyle}
        >
          <JobTitleInput
            type="text"
            placeholder={t("Change Campaign Name")}
            value={newCampaignName}
            onChange={handleCampaignNameChange}
            onBlur={saveNewCampaignName}
            autoFocus
          />
          <SaveJobTitleButton onClick={saveNewCampaignName}>
            <ArrowEnterIcon />
          </SaveJobTitleButton>
        </motion.div>
      </JobTitleContainer>
    ) : (
      <JobTitleContainer onClick={handleEditBasicInfo}>
        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={animationVariants}
          transition={{ duration: 0.5 }}
          style={animationContainerStyle}
        >
          <JobTitle>{newCampaignName}</JobTitle>
          <EditIcon color="var(--primary-blue-300)" size={16} strokeWidth={1.5} />
        </motion.div>
      </JobTitleContainer>
    );
  };

  return (
    <NavigationStepsWrapper>
      <Modal isOpen={editBasicInfo} setIsOpen={() => setEditBasicInfo(false)}>
        <EditCampaignDataModal onClose={() => setEditBasicInfo(false)} campaignId={campaignId} />
      </Modal>

      <StepsContainer>
        <StepJobTitleContainer>
          <Step>{step}</Step>
          {!hideSteps && renderJobTitleContainer()}
        </StepJobTitleContainer>
        {!hideSteps && (
          <>
            <HeaderDivider ml="30px" />

            <NavigationStepsContainer>
              <NavigationStepsButton
                isActive={stepName === "preview"}
                onClick={() => handleNavigation(`/assistant/preview/${campaignId}`)}
              >
                <BrushIcon
                  color="var(--primary-blue-300)"
                  opacity={stepName === "preview" ? 1 : 0.5}
                />
                <span>{t("Creation")}</span>
              </NavigationStepsButton>
              <LongRightArrowIcon />
              <NavigationStepsButton
                isActive={stepName === "overview"}
                onClick={() => handleNavigation(`/assistant/overview/${campaignId}`)}
              >
                <AnnouncementIcon
                  color="var(--primary-blue-300)"
                  opacity={stepName === "overview" ? 1 : 0.5}
                />
                <span>{t("Overview")}</span>
              </NavigationStepsButton>
              <LongRightArrowIcon />
              <NavigationStepsButton
                isActive={stepName === "budget-check"}
                onClick={() =>
                  handleNavigation(`/assistant/budget-check/${campaignId}?budgetCheck=true`)
                }
              >
                <RocketIcon
                  color="var(--primary-blue-300)"
                  opacity={stepName === "budget-check" ? 1 : 0.5}
                />
                <span>{t("Channels")}</span>
              </NavigationStepsButton>
            </NavigationStepsContainer>
          </>
        )}
      </StepsContainer>
      <EditBasicInfoContainer>
        {!hideSteps && (
          <EditBasicInfo onClick={() => setEditBasicInfo(!editBasicInfo)}>
            <ConfigIcon />
            <EditBasicInfoText>{t("Campaign Settings")}</EditBasicInfoText>
          </EditBasicInfo>
        )}
        <CloseButton onClick={onClose} darkMode={true}>
          <XIcon color="#fff" />
        </CloseButton>
      </EditBasicInfoContainer>
    </NavigationStepsWrapper>
  );
};

export default memo(
  withRouter(withRefluxStore(NavigationSteps, [AdAssistantStore], ["campaignData"]))
);
